import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { cn } from "../../lib/cn";

interface PopoverContextValue {
  open: boolean;
  setOpen: (v: boolean) => void;
  triggerRef: React.RefObject<HTMLElement | null>;
}

const PopoverContext = createContext<PopoverContextValue | null>(null);

function usePopoverContext() {
  const ctx = useContext(PopoverContext);
  if (!ctx) throw new Error("Popover compound components must be used inside <Popover>");
  return ctx;
}

export function Popover({
  children,
  open: openProp,
  onOpenChange,
  className,
}: {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const setOpen = useCallback(
    (value: boolean) => {
      onOpenChange?.(value);
      if (openProp === undefined) {
        setInternalOpen(value);
      }
    },
    [onOpenChange, openProp],
  );
  const triggerRef = useRef<HTMLElement | null>(null);

  return (
    <PopoverContext.Provider value={{ open, setOpen, triggerRef }}>
      <div className={cn("relative inline-block", className)}>{children}</div>
    </PopoverContext.Provider>
  );
}

export function PopoverTrigger({
  children,
  asChild,
  className,
}: {
  children: ReactNode;
  asChild?: boolean;
  className?: string;
}) {
  const { open, setOpen, triggerRef } = usePopoverContext();

  if (asChild) {
    return (
      <div
        ref={triggerRef as React.RefObject<HTMLDivElement>}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={className}
        onClick={() => setOpen(!open)}
      >
        {children}
      </div>
    );
  }

  return (
    <button
      ref={triggerRef as React.RefObject<HTMLButtonElement>}
      type="button"
      aria-expanded={open}
      aria-haspopup="listbox"
      className={cn("cursor-pointer", className)}
      onClick={() => setOpen(!open)}
    >
      {children}
    </button>
  );
}

function computePosition(
  trigger: DOMRect,
  content: DOMRect,
  side: "top" | "bottom" | "right",
  align: "start" | "center" | "end",
) {
  const gap = 4;
  let top = 0;
  let left = 0;

  if (side === "top") {
    top = trigger.top - content.height - gap;
  } else if (side === "right") {
    top =
      align === "end"
        ? trigger.bottom - content.height
        : align === "center"
          ? trigger.top + trigger.height / 2 - content.height / 2
          : trigger.top;
    left = trigger.right + gap;
  } else {
    top = trigger.bottom + gap;
  }

  if (side !== "right") {
    left =
      align === "end"
        ? trigger.right - content.width
        : align === "center"
          ? trigger.left + trigger.width / 2 - content.width / 2
          : trigger.left;
  }

  const pad = 8;
  left = Math.min(Math.max(pad, left), window.innerWidth - content.width - pad);
  top = Math.min(Math.max(pad, top), window.innerHeight - content.height - pad);

  return { top, left };
}

export function PopoverContent({
  children,
  className,
  align = "start",
  side = "bottom",
}: {
  children: ReactNode;
  className?: string;
  align?: "start" | "center" | "end";
  side?: "top" | "bottom" | "right";
}) {
  const { open, setOpen, triggerRef } = usePopoverContext();
  const contentRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const updatePosition = useCallback(() => {
    const triggerEl = triggerRef.current;
    const contentEl = contentRef.current;
    if (!triggerEl || !contentEl) return;
    setCoords(computePosition(triggerEl.getBoundingClientRect(), contentEl.getBoundingClientRect(), side, align));
  }, [align, side, triggerRef]);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onReposition = () => updatePosition();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (contentRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [open, setOpen, triggerRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={contentRef}
      className={cn(
        "fixed z-[200] rounded-lg border border-sidebar-border bg-card p-2 shadow-[0_8px_24px_rgba(27,27,24,0.12)]",
        !coords && "invisible",
        className,
      )}
      style={coords ? { top: coords.top, left: coords.left } : { top: 0, left: 0 }}
    >
      {children}
    </div>,
    document.body,
  );
}
